/**
 * Filters and search.
 *
 * Everything here writes to the URL (see useFilters), so a filtered view is a
 * link. Filters combine: each one narrows what the others left.
 *
 * On a narrow screen the whole set collapses behind one button showing how many
 * are active, because six filter controls above the list would push the list --
 * the thing you came for -- off the screen.
 */

import { useEffect, useState } from 'react'

import type { Category, ItemPriority, ItemStatus, TagWithCount } from '@/api/types'
import { PRIORITIES, STATUSES } from '@/api/types'
import { DUE_LABEL, DUE_WINDOWS, type DueWindow, type Filters } from '@/hooks/useFilters'
import { PRIORITY_LABEL, STATUS_LABEL, pluralize } from '@/lib/format'

interface FilterBarProps {
  filters: Filters
  active: boolean
  categories: Category[]
  tags: TagWithCount[]
  matchCount: number
  update: (patch: Partial<Filters>) => void
  toggle: <K extends 'priority' | 'status' | 'tagIds'>(
    key: K,
    value: Filters[K][number],
  ) => void
  clear: () => void
}

function Chip({
  label,
  selected,
  onClick,
  color,
}: {
  label: string
  selected: boolean
  onClick: () => void
  color?: string | null
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={[
        'pill border transition-colors',
        selected
          ? 'border-navy bg-navy text-white'
          : 'border-line-strong bg-surface text-ink-2 hover:border-navy hover:text-navy',
      ].join(' ')}
      style={selected && color ? { backgroundColor: color, borderColor: color } : undefined}
    >
      {label}
    </button>
  )
}

export function FilterBar({
  filters,
  active,
  categories,
  tags,
  matchCount,
  update,
  toggle,
  clear,
}: FilterBarProps) {
  const [open, setOpen] = useState(false)
  const [draftQuery, setDraftQuery] = useState(filters.q)

  // Debounced: one request per pause in typing rather than one per keystroke,
  // and the URL does not churn while a word is half-written.
  useEffect(() => {
    if (draftQuery === filters.q) return
    const timer = setTimeout(() => update({ q: draftQuery }), 250)
    return () => clearTimeout(timer)
  }, [draftQuery, filters.q, update])

  // Keep the box in step when the URL changes from elsewhere (a stats-strip
  // link, the back button).
  useEffect(() => setDraftQuery(filters.q), [filters.q])

  const activeCount =
    (filters.categoryId !== null ? 1 : 0) +
    (filters.subcategoryId !== null ? 1 : 0) +
    (filters.due !== null ? 1 : 0) +
    filters.priority.length +
    filters.status.length +
    filters.tagIds.length

  const selectedCategory = categories.find((c) => c.id === filters.categoryId)

  return (
    <section className="card p-3" aria-label="Filters and search">
      <div className="flex items-center gap-2">
        <input
          type="search"
          className="field flex-1"
          placeholder="Search titles, descriptions and notes..."
          value={draftQuery}
          onChange={(event) => setDraftQuery(event.target.value)}
          aria-label="Search"
        />
        <button
          type="button"
          className="btn btn-neutral btn-sm shrink-0"
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
        >
          Filters{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>
        {active && (
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-navy hover:underline"
            onClick={clear}
          >
            Clear
          </button>
        )}
      </div>

      {active && (
        <p className="mt-2 text-xs text-ink-3">
          {matchCount} {pluralize(matchCount, 'item')} match
          {matchCount === 1 ? 'es' : ''} these filters.
          {filters.q && ' Search includes archived items.'}
        </p>
      )}

      {open && (
        <div className="mt-3 flex flex-col gap-3 border-t border-line pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs font-semibold text-ink-2">Due</span>
            {DUE_WINDOWS.map((window) => (
              <Chip
                key={window}
                label={DUE_LABEL[window as DueWindow]}
                selected={filters.due === window}
                onClick={() => update({ due: filters.due === window ? null : window })}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs font-semibold text-ink-2">Priority</span>
            {PRIORITIES.map((priority: ItemPriority) => (
              <Chip
                key={priority}
                label={PRIORITY_LABEL[priority]}
                selected={filters.priority.includes(priority)}
                onClick={() => toggle('priority', priority)}
              />
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs font-semibold text-ink-2">Status</span>
            {STATUSES.map((status: ItemStatus) => (
              <Chip
                key={status}
                label={STATUS_LABEL[status]}
                selected={filters.status.includes(status)}
                onClick={() => toggle('status', status)}
              />
            ))}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="w-20 shrink-0 text-xs font-semibold text-ink-2">Tags</span>
              {tags.map((tag) => (
                <Chip
                  key={tag.id}
                  label={`${tag.name} (${tag.item_count})`}
                  selected={filters.tagIds.includes(tag.id)}
                  onClick={() => toggle('tagIds', tag.id)}
                  color={tag.color}
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="w-20 shrink-0 text-xs font-semibold text-ink-2">Where</span>
            <select
              className="field w-auto"
              value={filters.categoryId ?? ''}
              onChange={(event) =>
                // Changing category clears any subcategory, which may well
                // belong to a different one -- leaving it would produce an
                // impossible pair that always matches nothing.
                update({
                  categoryId: event.target.value ? Number(event.target.value) : null,
                  subcategoryId: null,
                })
              }
              aria-label="Category"
            >
              <option value="">All categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <select
              className="field w-auto"
              value={filters.subcategoryId ?? ''}
              onChange={(event) =>
                update({
                  subcategoryId: event.target.value ? Number(event.target.value) : null,
                })
              }
              aria-label="Subcategory"
            >
              <option value="">All subcategories</option>
              {(selectedCategory ? [selectedCategory] : categories).flatMap((category) =>
                category.subcategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {selectedCategory ? sub.name : `${category.name} › ${sub.name}`}
                  </option>
                )),
              )}
            </select>
          </div>
        </div>
      )}
    </section>
  )
}
